<template>
    <div id="sender-mail-address">
        <div class="section-title">
            Gönderici Mail Adresleri
            <div class="add-button" @click="openModal()">
                <span class="material-icons">add</span>
                Gönderici Adresi Ekle
            </div>
        </div>

        <div class="sender-table" v-if="data.length > 0">
            <div class="table-header">
                <div class="table-cell mail-cell">Mail Adresi</div>
                <div class="table-cell">Yönlendirme</div>
                <div class="table-cell">Doğrulanmış</div>
                <div class="table-cell" v-if="isCanBeChangeDefault(selectedMailAddress)">
                    Varsayılan
                    <a-tooltip
                        placement="top"
                        title="Varsayılan olarak işaretlenen e-posta adresi, tüm mail gönderim arayüzlerinde otomatik olarak seçili gelecektir."
                    >
                        <span class="material-icons info-icon">info</span>
                    </a-tooltip>
                </div>
                <div class="table-cell actions-cell">İşlemler</div>
            </div>
            <div class="table-body">
                <div class="table-row" v-for="address in data" :key="address._id">
                    <div class="table-cell mail-cell">
                        <a-tooltip :title="address.mail">
                            {{ address.mail }}
                        </a-tooltip>
                    </div>
                    <div class="table-cell">
                        <div v-if="address.isForward" class="status-badge success">
                            <span class="material-icons">verified</span>
                            Doğrulandı
                        </div>
                        <a-tooltip v-else :title="'Yönlendirme işleminden sonra otomatik olarak güncellenecektir.'">
                            <div class="status-badge warning">Yönlendirilmedi</div>
                        </a-tooltip>
                    </div>
                    <div class="table-cell">
                        <div
                            v-if="!address.isSenderMail || (!!address.isSenderMail && address.isSenderVerified)"
                            class="status-badge success"
                        >
                            <span class="material-icons">verified</span>
                            Doğrulandı
                        </div>
                        <div v-else class="status-badge warning">
                            <span>Doğrulanmadı</span>
                            <span class="material-icons refresh-icon" @click="isVerifiedControl(address)">refresh</span>
                        </div>
                    </div>
                    <div class="table-cell">
                        <input
                            v-if="isCanBeChangeDefault(address)"
                            :disabled="
                                address.isSenderMail ? address.isSenderVerified === false : address.isVerified === false
                            "
                            type="radio"
                            :checked="address.isDefault"
                            @click="changeDefaultMail(address)"
                        />
                    </div>
                    <div class="table-cell actions-cell">
                        <SupsisButton danger ghost @click="deleteSenderMail(address)" :disabled="!address.isSenderMail">
                            Sil
                        </SupsisButton>
                    </div>
                </div>
            </div>
            <div class="table-footer">
                <a-pagination
                    :current="currentPage"
                    :pageSize="perPage"
                    :total="total"
                    @change="pageChange"
                    @showSizeChange="onShowSizeChange"
                    :pageSizeOptions="pageSizes"
                    showSizeChanger
                />
            </div>
        </div>
        <div class="empty-state" v-else>
            <span class="material-icons">mail_outline</span>
            <div class="empty-text">Henüz gönderici adresi bulunmamaktadır</div>
            <SupsisButton primary @click="openModal()"> Gönderici Adresi Ekle</SupsisButton>
        </div>

        <Modal2
            v-if="isCreateModal"
            title="Gönderici Adresi Ekle"
            @cancel="isCreateModal = false"
            :show-footer="false"
            :show-title="false"
            class="sender-mail-modal"
        >
            <div v-if="current === 0">
                <div class="modal-header">
                    <h3>Gönderici Adresi Ekle</h3>
                </div>
                <div class="modal-form">
                    <div class="form-description">
                        Bu mail adresi, müşterilerinize gönderilen maillerin gönderen kısmında görünecektir.
                    </div>
                    <div class="form-group">
                        <label for="mailAddress">Mail Adresi</label>
                        <div class="input-container" :class="{ error: mailAddress && !isValidEmail }">
                            <span class="input-icon">
                                <span class="material-icons">mail</span>
                            </span>
                            <input
                                id="mailAddress"
                                v-model="mailAddress"
                                class="form-input"
                                placeholder="ornek@domain.com"
                                @keydown.enter="verifySenderMail"
                            />
                        </div>
                        <div class="error-message" v-if="mailAddress && !isValidEmail">
                            Lütfen geçerli bir e-posta adresi girin
                        </div>
                    </div>
                    <div class="modal-footer">
                        <SupsisButton @click="isCreateModal = false">İptal</SupsisButton>
                        <SupsisButton primary :disabled="!isValidEmail" @click="verifySenderMail()">
                            Doğrula
                        </SupsisButton>
                    </div>
                </div>
            </div>
            <div v-if="current === 1">
                <div class="modal-header">
                    <h3>Mail Yönlendirme</h3>
                </div>
                <div class="modal-video-container">
                    <gif-player :src="getGifSrc()"></gif-player>
                </div>
                <div class="modal-footer">
                    <SupsisButton primary @click="isCreateModal = false">Tamam</SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { log2 } from "../../../../utils/log2";
import MailIcon from "../icons/MailIcon.vue";
import MicrosoftIcon from "../icons/MicrosoftIcon.vue";
import { sendSocketMessage } from "../../../../utils/socketStream";
import CONFIG from "../../../../config/config";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisButton from "../../../Pages/Settings/components/SupsisButton.vue";

const log = log2("SenderMailAddress.vue");

// Email validasyon fonksiyonu
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export default Vue.extend({
    name: "SenderMailAddress",
    components: {
        MailIcon,
        MicrosoftIcon,
        Modal2,
        SupsisButton,
    },
    data() {
        return {
            current: 0,
            currentSort: {
                columnKey: "createdAt",
                order: "ascend",
            },
            loading: false,
            data: [],
            renderCount: 0,
            perPage: 10,
            pageSizes: ["10", "25", "50", "100"],
            total: 0,
            currentPage: 1,
            isCreateModal: false,
            mailAddress: "",
            selectedMailAddress: "",
        };
    },
    computed: {
        ...mapState({
            newSenderMail: (state) => state.mail.newSenderMail,
            updateSenderMail: (state) => state.mail.updateSenderMail,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            domainIsCreated: (state) => state.mail.domainIsCreated,
            allMailAddresses: (state) => state.mail.allMailAddresses,
            newForwardMailAddress: (state) => state.mail.newForwardMailAddress,
            emailDomain: (state) =>
                state.site.site.alias === "supsis"
                    ? CONFIG.EMAIL_DOMAIN
                    : `${state.site.site.alias}.${CONFIG.EMAIL_DOMAIN}`,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        isMailUpdatePermission() {
            return this.checkPermission(PERMISSION_TYPES["mail.update"].tr.name);
        },
        isMailDeletePermission() {
            return this.checkPermission(PERMISSION_TYPES["mail.delete"].tr.name);
        },
        isValidEmail() {
            return this.mailAddress && isValidEmail(this.mailAddress);
        },
    },
    methods: {
        ...mapActions({
            getSenderMailAddress: "mail/getSenderMailAddress",
            changeDefaultMailAddress: "mail/changeDefaultMailAddress",
            deleteSenderMailAction: "mail/deleteSenderMail",
        }),
        ...mapMutations({
            DELETE_MAIL_ADDRESS: "mail/DELETE_MAIL_ADDRESS",
        }),
        isCanBeChangeDefault(mail) {
            const userSubjects = this.user.subjects || [];
            const isUserSubject = userSubjects.find((x) => x._id === mail?.department?._id);
            const isUserMail = mail?.user?._id === this.user._id;
            return isUserMail || isUserSubject;
        },
        isVerifiedControl(record) {
            sendSocketMessage("email is_verified_sender_mail", {
                siteId: this.site._id,
                senderMailId: record._id,
                identity: record.mail,
            });
        },
        openModal() {
            this.isCreateModal = true;
            this.current = 0;
            this.mailAddress = "";
        },
        getGifSrc() {
            return `https://static.supsis.live/common/email/screenshot/gmail.mp4`;
        },
        deleteSenderMail(record) {
            if (!this.isMailDeletePermission) {
                this.$message.error("Bu işlem için yetkiniz bulunmamaktadır");
                return;
            } else {
                console.log("deleteSenderMail-->", { record });
                this.$confirm({
                    title: "Mail Adresini Sil",
                    content: "Bu mail adresini silmek istediğinizden emin misiniz?",
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: () => {
                        sendSocketMessage(
                            record.isSenderMail ? "email delete_sender_email" : "email delete_user_email",
                            {
                                siteId: this.site._id,
                                mailAddressId: record._id,
                                identity: record.mail,
                            }
                        );
                        this.data = this.data.filter((item) => item._id !== record._id);
                        if (record.isDefault) {
                            this.data = this.data.map((item) => {
                                item.isDefault = false;
                                return item;
                            });
                        }
                        this.$message.success("Mail adresi başarıyla silindi");
                    },
                });
            }
        },
        changeDefaultMail(record) {
            if (!!record.isDefault) return;
            this.changeDefaultMailAddress({ userId: this.user._id, mailAddressId: record._id }).then((res) => {
                this.data = this.data.map((item) => {
                    item.isDefault = item._id === record._id;
                    return item;
                });
                this.$message.success("Varsayılan mail adresi başarıyla değiştirildi");
            });
        },
        verifySenderMail() {
            console.log(
                "verifySenderMail-->",
                this.data.map((x) => x.mail)
            );
            const isCreatedBefore = this.data.filter((x) => x.mail === this.mailAddress).length !== 0;
            if (this.mailAddress.length === 0 || isCreatedBefore) {
                this.$message.error("Bu mail adresi zaten kayıtlı");
            } else {
                this.current = 1;
                const data = {
                    siteId: this.site._id,
                    userId: this.user._id,
                    identity: this.mailAddress,
                    emailDomain: this.emailDomain,
                    forwardTo: this.selectedMailAddress.mail,
                };
                console.log("verifySenderMail data..", data);
                sendSocketMessage("email create_sender_user_email", data);
                this.$message.success("Lütfen doğrulama için linke tıklayın");
                this.mailAddress = "";
            }
        },
        pageChange(page) {
            this.currentPage = page;
        },
        onShowSizeChange(current, size) {
            this.currentPage = 1;
            this.perPage = size;
        },
        async fetchPagedData() {
            this.loading = true;
            await this.fetch();
        },
        fetch() {
            this.loading = true;
            this.getSenderMailAddress({
                userId: this.user._id,
                selectedMailAddressId: this.selectedMailAddress._id,
                perPage: this.perPage,
                pageCount: this.currentPage,
                forwardTo: this.selectedMailAddress.mail,
            }).then((res) => {
                console.log("listSenderMailAddresses res..", { res });
                this.total = res.count;
                this.data = res.mailAddresses;
                this.loading = false;
            });
        },
    },
    async mounted() {
        console.log("mounted this.allMailAddresses..", this.allMailAddresses);
        if (this.allMailAddresses.length !== 0) {
            this.selectedMailAddress = this.allMailAddresses.filter(
                (x) => x._id === this.$route.params.id || (!!x.department && x.department._id === this.$route.params.id)
            )[0];
            await this.fetchPagedData();
        }
    },
    watch: {
        allMailAddresses(newValue, oldValue) {
            console.log("allMailAddresses watch..", { newValue, oldValue });
            this.selectedMailAddress = newValue.filter(
                (x) => x._id === this.$route.params.id || (!!x.department && x.department._id === this.$route.params.id)
            )[0];
            this.fetch();
        },
        newForwardMailAddress(value) {
            console.log("newForwardMailAddress watch..", value);
            this.data = this.data.map((x) => {
                if (x._id === value._id) {
                    return value;
                } else {
                    return x;
                }
            });
        },
        updateSenderMail(newValue, oldValue) {
            if (!newValue) return;
            this.data = this.data.map((item) => {
                if (item._id === newValue._id) {
                    item = newValue;
                }
                return item;
            });
        },
        newSenderMail(newValue) {
            this.data = [...this.data, newValue];
            this.total += 1;
        },
        currentPage() {
            this.fetch();
        },
        perPage() {
            this.fetch();
        },
    },
});
</script>

<style scoped lang="scss">
#sender-mail-address {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    position: relative;
    width: 100%;
    height: 100%;
    padding: 20px;

    .section-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 20px;
        color: #22272d;

        .add-button {
            display: flex;
            align-items: center;
            background-color: #22272d;
            color: white;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;

            .material-icons {
                font-size: 18px;
                margin-right: 5px;
            }

            &:hover {
                opacity: 0.9;
            }
        }
    }

    .sender-table {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .table-header {
            display: flex;
            background-color: #f9f9f9;
            font-weight: 600;
            color: #22272d;

            .table-cell {
                padding: 12px 15px;
                border-bottom: 1px solid #e0e0e0;

                .info-icon {
                    font-size: 16px;
                    color: #999;
                    margin-left: 5px;
                    cursor: help;
                    vertical-align: middle;

                    &:hover {
                        color: #22272d;
                    }
                }
            }
        }

        .table-body {
            .table-row {
                display: flex;
                border-bottom: 1px solid #e0e0e0;

                &:hover {
                    background-color: #f9f9f9;
                }

                &:last-child {
                    border-bottom: none;
                }
            }
        }

        .table-cell {
            flex: 1;
            padding: 12px 15px;
            display: flex;
            align-items: center;

            &.mail-cell {
                flex: 2;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                color: #22272d;
            }

            &.actions-cell {
                justify-content: flex-end;
            }
        }

        .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;

            &.success {
                background-color: #f6ffed;
                color: #52c41a;
                border: 1px solid #b7eb8f;

                .material-icons {
                    font-size: 16px;
                    margin-right: 4px;
                }
            }

            &.warning {
                background-color: #fff7e6;
                color: #fa8c16;
                border: 1px solid #ffd591;
                display: flex;
                align-items: center;

                .refresh-icon {
                    cursor: pointer;
                    font-size: 16px;
                    margin-left: 4px;

                    &:hover {
                        color: #d46b08;
                    }
                }
            }
        }

        .table-footer {
            padding: 12px 15px;
            display: flex;
            justify-content: flex-end;
            border-top: 1px solid #e0e0e0;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .material-icons {
            font-size: 48px;
            color: #ccc;
            margin-bottom: 16px;
        }

        .empty-text {
            font-size: 16px;
            color: #666;
            margin-bottom: 16px;
        }
    }

    .modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid #e0e0e0;

        h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #22272d;
        }
    }

    .modal-footer {
        padding: 15px 20px;
        border-top: 1px solid #e0e0e0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .modal-video-container {
        padding: 20px;
        display: flex;
        justify-content: center;
        width: 100%;
    }

    .modal-form {
        padding: 20px;

        .form-description {
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
            background-color: #f9f9f9;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #22272d;
        }

        .form-group {
            margin-bottom: 24px;

            label {
                display: block;
                margin-bottom: 8px;
                font-weight: 500;
                color: #22272d;
                font-size: 14px;
            }

            .input-container {
                position: relative;
                display: flex;
                align-items: center;

                &.error {
                    input,
                    .form-input {
                        border-color: #f5222d;

                        &:focus {
                            box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.1);
                        }
                    }

                    .input-icon {
                        .material-icons {
                            color: #f5222d;
                        }
                    }
                }

                .input-icon {
                    position: absolute;
                    left: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        color: #22272d;
                        font-size: 18px;
                    }
                }

                input,
                .form-input {
                    width: 100%;
                    padding: 12px 12px 12px 42px;
                    border: 1px solid #d9d9d9;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: all 0.3s ease;

                    &:focus {
                        outline: none;
                        border-color: #22272d;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
                    }

                    &::placeholder {
                        color: #bbb;
                    }

                    &[readonly] {
                        background-color: #f5f5f5;
                        cursor: not-allowed;
                    }
                }
            }

            .error-message {
                color: #f5222d;
                font-size: 12px;
                margin-top: 4px;
            }
        }
    }
}
</style>

<style lang="scss">
.sender-mail-modal {
    .modal-form {
        padding: 20px;

        .form-description {
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
            background-color: #f9f9f9;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #22272d;
        }

        .form-group {
            margin-bottom: 24px;

            label {
                display: block;
                margin-bottom: 8px;
                font-weight: 500;
                color: #22272d;
                font-size: 14px;
            }

            .input-container {
                position: relative;
                display: flex;
                align-items: center;

                &.error {
                    input,
                    .form-input {
                        border-color: #f5222d;

                        &:focus {
                            box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.1);
                        }
                    }

                    .input-icon {
                        .material-icons {
                            color: #f5222d;
                        }
                    }
                }

                .input-icon {
                    position: absolute;
                    left: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        color: #22272d;
                        font-size: 18px;
                    }
                }

                input,
                .form-input {
                    width: 100%;
                    padding: 12px 12px 12px 42px;
                    border: 1px solid #d9d9d9;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: all 0.3s ease;

                    &:focus {
                        outline: none;
                        border-color: #22272d;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
                    }

                    &::placeholder {
                        color: #bbb;
                    }

                    &[readonly] {
                        background-color: #f5f5f5;
                        cursor: not-allowed;
                    }
                }
            }

            .error-message {
                color: #f5222d;
                font-size: 12px;
                margin-top: 4px;
            }
        }
    }

    .modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid #e0e0e0;

        h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #22272d;
        }
    }

    .modal-footer {
        padding: 15px 20px;
        border-top: 1px solid #e0e0e0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .modal-video-container {
        padding: 20px;
        display: flex;
        justify-content: center;
        width: 100%;
    }
}
</style>
